import {
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';

import {
  CommonModule
} from '@angular/common';

import {
  FormsModule
} from '@angular/forms';

import {
  interval,
  Subject,
  takeUntil
} from 'rxjs';

import {
  ApiService
} from '../../core/api.service';

import {
  OpenSessionPayload,
  PaymentMethod,
  RegisterPaymentPayload,
  RestaurantTable,
  TableManagementService,
  TableSession
} from '../../core/table-management.service';


interface RestaurantOption {
  id: number;
  name: string;
}


interface TableServiceRequest {
  id: number;

  restaurantId: number;
  tableId: number;

  sessionId?: number | null;

  type:
    | 'WAITER'
    | 'OPEN_SESSION';

  status:
    | 'PENDING'
    | 'ATTENDED'
    | 'CANCELED';

  createdAt: string;

  attendedAt?: string | null;

  attendedById?: number | null;

  table?: {
    id: number;
    name: string;
    number?: number | null;
    area?: string | null;
    status?: string;
  };

  session?: any;

  attendedBy?: {
    id: number;
    name: string;
    email?: string;
  } | null;
}


@Component({
  selector: 'app-tables',

  standalone: true,

  imports: [
    CommonModule,
    FormsModule
  ],

  templateUrl:
    './tables.component.html',

  styleUrl:
    './tables.component.css'
})
export class TablesComponent
  implements OnInit, OnDestroy {

  private readonly destroy$ =
    new Subject<void>();


  restaurants:
    RestaurantOption[] = [];

  tables:
    RestaurantTable[] = [];


  /*
   * Chamados vindos do QR Code.
   */
  serviceRequests:
    TableServiceRequest[] = [];


  restaurantId:
    number | null = null;


  selectedTable:
    RestaurantTable | null = null;

  selectedSession:
    TableSession | null = null;


  loading = false;

  loadingSession = false;

  loadingRequests = false;

  saving = false;


  errorMessage = '';

  successMessage = '';


  statusFilter = '';

  searchText = '';


  showOpenSessionModal = false;

  showSessionModal = false;

  showPaymentModal = false;

  showTransferModal = false;


  transferTargetTableId:
    number | null = null;


  /*
   * Guarda as solicitações
   * que estão sendo atualizadas.
   */
  attendingRequestIds =
    new Set<number>();


  openSessionForm = {
    customerName: '',
    customerCount: 1,
    notes: '',
    serviceFeeRate: 10,
    discountAmount: 0
  };


  paymentForm: {
    method: PaymentMethod;
    amount: number | null;
    notes: string;
    transactionCode: string;
  } = {
    method: 'PIX',
    amount: null,
    notes: '',
    transactionCode: ''
  };


  constructor(
    private readonly tableService:
      TableManagementService,

    private readonly api:
      ApiService
  ) {}


  ngOnInit(): void {

    this.loadRestaurants();


    /*
     * Verifica novos chamados
     * automaticamente.
     *
     * Assim o garçom não precisa
     * atualizar a tela manualmente.
     */
    interval(5000)
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe(() => {

        if (
          this.restaurantId
        ) {

          this.loadServiceRequests(
            false
          );

        }

      });
  }


  ngOnDestroy(): void {

    this.destroy$.next();

    this.destroy$.complete();
  }


  /*
   * ==================================================
   * RESTAURANTE
   * ==================================================
   */

  loadRestaurants(): void {

    const savedRestaurantId =
      Number(
        localStorage.getItem(
          'restaurantId'
        )
      );


    if (
      Number.isInteger(
        savedRestaurantId
      ) &&
      savedRestaurantId > 0
    ) {

      this.restaurantId =
        savedRestaurantId;

      this.loadTables();

    }
  }


  selectRestaurant(): void {

    this.tables = [];

    this.serviceRequests = [];

    this.selectedTable = null;

    this.selectedSession = null;

    this.loadTables();
  }


  /*
   * ==================================================
   * MESAS
   * ==================================================
   */

  loadTables(): void {

    if (
      !this.restaurantId
    ) {
      return;
    }


    this.loading = true;

    this.clearMessages();


    this.tableService
      .listTables(
        this.restaurantId
      )
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe({

        next: tables => {

          this.tables =
            tables || [];

          this.loading =
            false;


          /*
           * Carrega também os
           * chamados pendentes.
           */
          this.loadServiceRequests();

        },

        error: error => {

          this.loading =
            false;


          this.errorMessage =
            error?.error?.message ||
            'Não foi possível carregar as mesas.';

        }

      });
  }


  /*
   * ==================================================
   * CHAMADOS / SOLICITAÇÕES DAS MESAS
   * ==================================================
   */

  loadServiceRequests(
    showLoading = true
  ): void {

    if (
      !this.restaurantId
    ) {
      return;
    }


    if (
      showLoading
    ) {

      this.loadingRequests =
        true;

    }


    this.api
      .get<
        TableServiceRequest[]
      >(
        `/restaurants/${this.restaurantId}/table-service-requests?status=PENDING`
      )
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe({

        next: requests => {

          this.serviceRequests =
            requests || [];

          this.loadingRequests =
            false;

        },

        error: error => {

          this.loadingRequests =
            false;


          /*
           * Não bloqueia a tela de mesas
           * se apenas os chamados falharem.
           */
          console.error(
            'Erro ao carregar solicitações das mesas:',
            error
          );

        }

      });
  }


  getRequestsForTable(
    tableId: number
  ): TableServiceRequest[] {

    return this
      .serviceRequests
      .filter(
        request =>
          Number(
            request.tableId
          ) ===
            Number(
              tableId
            ) &&
          request.status ===
            'PENDING'
      );
  }


  getWaiterRequest(
    tableId: number
  ):
    TableServiceRequest |
    null {

    return (
      this
        .getRequestsForTable(
          tableId
        )
        .find(
          request =>
            request.type ===
            'WAITER'
        ) ||
      null
    );
  }


  getOpenSessionRequest(
    tableId: number
  ):
    TableServiceRequest |
    null {

    return (
      this
        .getRequestsForTable(
          tableId
        )
        .find(
          request =>
            request.type ===
            'OPEN_SESSION'
        ) ||
      null
    );
  }


  hasPendingRequest(
    tableId: number
  ): boolean {

    return (
      this
        .getRequestsForTable(
          tableId
        )
        .length >
      0
    );
  }


  get pendingRequestsCount():
    number {

    return this
      .serviceRequests
      .filter(
        request =>
          request.status ===
          'PENDING'
      )
      .length;
  }


  get waiterRequestsCount():
    number {

    return this
      .serviceRequests
      .filter(
        request =>
          request.status ===
            'PENDING' &&
          request.type ===
            'WAITER'
      )
      .length;
  }


  get openSessionRequestsCount():
    number {

    return this
      .serviceRequests
      .filter(
        request =>
          request.status ===
            'PENDING' &&
          request.type ===
            'OPEN_SESSION'
      )
      .length;
  }


  getRequestAge(
    request:
      TableServiceRequest
  ): string {

    if (
      !request?.createdAt
    ) {
      return '';
    }


    const createdAt =
      new Date(
        request.createdAt
      )
        .getTime();


    const difference =
      Date.now() -
      createdAt;


    const minutes =
      Math.max(
        0,
        Math.floor(
          difference /
          60000
        )
      );


    if (
      minutes < 1
    ) {
      return 'agora';
    }


    if (
      minutes < 60
    ) {

      return (
        `há ${minutes} min`
      );

    }


    const hours =
      Math.floor(
        minutes /
        60
      );


    if (
      hours === 1
    ) {
      return 'há 1 hora';
    }


    return (
      `há ${hours} horas`
    );
  }


  attendServiceRequest(
    request:
      TableServiceRequest
  ): void {

    if (
      !this.restaurantId ||
      !request?.id
    ) {
      return;
    }


    if (
      this
        .attendingRequestIds
        .has(
          request.id
        )
    ) {
      return;
    }


    this
      .attendingRequestIds
      .add(
        request.id
      );


    this.api
      .patch<any>(
        `/restaurants/${this.restaurantId}/table-service-requests/${request.id}/attend`,
        {}
      )
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe({

        next: response => {

          this
            .attendingRequestIds
            .delete(
              request.id
            );


          /*
           * Remove imediatamente
           * da interface.
           */
          this.serviceRequests =
            this.serviceRequests
              .filter(
                item =>
                  item.id !==
                  request.id
              );


          this.successMessage =
            response?.message ||
            'Solicitação marcada como atendida.';

        },

        error: error => {

          this
            .attendingRequestIds
            .delete(
              request.id
            );


          this.errorMessage =
            error?.error?.message ||
            'Não foi possível atender a solicitação.';

        }

      });
  }


  isAttendingRequest(
    requestId: number
  ): boolean {

    return this
      .attendingRequestIds
      .has(
        requestId
      );
  }


  private attendOpenSessionRequest(
    tableId: number
  ): void {

    const request =
      this.getOpenSessionRequest(
        tableId
      );


    if (
      !request
    ) {
      return;
    }


    this.attendServiceRequest(
      request
    );
  }


  /*
   * ==================================================
   * ABRIR COMANDA
   * ==================================================
   */

  openSessionModal(
    table:
      RestaurantTable
  ): void {

    this.clearMessages();


    if (
      table.status !==
      'AVAILABLE'
    ) {

      this.errorMessage =
        'Somente mesas disponíveis podem receber uma nova comanda.';

      return;
    }


    this.selectedTable =
      table;


    this.openSessionForm = {
      customerName: '',
      customerCount: 1,
      notes: '',
      serviceFeeRate: 10,
      discountAmount: 0
    };


    this.showOpenSessionModal =
      true;
  }


  openSession(): void {

    if (
      !this.restaurantId ||
      !this.selectedTable
    ) {
      return;
    }


    /*
     * Guarda o ID porque
     * selectedTable pode ser alterado
     * após fechar o modal.
     */
    const tableId =
      this.selectedTable.id;


    const customerCount =
      Number(
        this.openSessionForm
          .customerCount
      );


    if (
      !Number.isInteger(
        customerCount
      ) ||
      customerCount <= 0
    ) {

      this.errorMessage =
        'Informe uma quantidade válida de clientes.';

      return;
    }


    const payload:
      OpenSessionPayload = {

      tableId:
        this.selectedTable.id,


      customerName:
        this.openSessionForm
          .customerName
          .trim() ||
        undefined,


      customerCount,


      notes:
        this.openSessionForm
          .notes
          .trim() ||
        undefined,


      serviceFeeRate:
        Number(
          this.openSessionForm
            .serviceFeeRate ||
          0
        ),


      discountAmount:
        Number(
          this.openSessionForm
            .discountAmount ||
          0
        )

    };


    this.saving = true;

    this.clearMessages();


    this.tableService
      .openSession(
        this.restaurantId,
        payload
      )
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe({

        next: () => {

          this.saving =
            false;


          this.showOpenSessionModal =
            false;


          this.successMessage =
            'Comanda aberta com sucesso.';


          /*
           * Se o próprio cliente
           * havia solicitado abertura,
           * finaliza esse chamado.
           */
          this.attendOpenSessionRequest(
            tableId
          );


          this.loadTables();

        },

        error: error => {

          this.saving =
            false;


          this.errorMessage =
            error?.error?.message ||
            'Não foi possível abrir a comanda.';

        }

      });
  }


  /*
   * ==================================================
   * VISUALIZAR COMANDA
   * ==================================================
   */

  viewSession(
    table:
      RestaurantTable
  ): void {

    const sessionId =
      table.activeSession?.id;


    if (
      !this.restaurantId ||
      !sessionId
    ) {

      this.errorMessage =
        'A mesa não possui uma comanda ativa.';

      return;
    }


    this.selectedTable =
      table;


    this.selectedSession =
      null;


    this.showSessionModal =
      true;


    this.loadingSession =
      true;


    this.clearMessages();


    this.tableService
      .getSession(
        this.restaurantId,
        sessionId
      )
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe({

        next: session => {

          this.selectedSession =
            session;


          this.loadingSession =
            false;

        },

        error: error => {

          this.loadingSession =
            false;


          this.errorMessage =
            error?.error?.message ||
            'Não foi possível carregar a comanda.';

        }

      });
  }


  refreshSelectedSession():
    void {

    if (
      !this.restaurantId ||
      !this.selectedSession
    ) {
      return;
    }


    this.loadingSession =
      true;


    this.tableService
      .getSession(
        this.restaurantId,
        this.selectedSession.id
      )
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe({

        next: session => {

          this.selectedSession =
            session;


          this.loadingSession =
            false;

        },

        error: error => {

          this.loadingSession =
            false;


          this.errorMessage =
            error?.error?.message ||
            'Não foi possível atualizar a comanda.';

        }

      });
  }


  /*
   * ==================================================
   * SOLICITAR CONTA
   * ==================================================
   */

  requestBill(): void {

    if (
      !this.restaurantId ||
      !this.selectedSession
    ) {
      return;
    }


    const confirmed =
      window.confirm(
        'Deseja solicitar a conta desta mesa?'
      );


    if (
      !confirmed
    ) {
      return;
    }


    this.saving = true;

    this.clearMessages();


    this.tableService
      .requestBill(
        this.restaurantId,
        this.selectedSession.id
      )
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe({

        next: () => {

          this.saving =
            false;


          this.successMessage =
            'Conta solicitada com sucesso.';


          this.refreshSelectedSession();

          this.loadTables();

        },

        error: error => {

          this.saving =
            false;


          this.errorMessage =
            error?.error?.message ||
            'Não foi possível solicitar a conta.';

        }

      });
  }


  /*
   * ==================================================
   * PAGAMENTO
   * ==================================================
   */

  openPaymentModal(): void {

    if (
      !this.selectedSession
    ) {
      return;
    }


    this.paymentForm = {

      method:
        'PIX',

      amount:
        this.remainingAmount,

      notes:
        '',

      transactionCode:
        ''

    };


    this.showPaymentModal =
      true;
  }


  registerPayment(): void {

    if (
      !this.restaurantId ||
      !this.selectedSession
    ) {
      return;
    }


    const amount =
      Number(
        this.paymentForm
          .amount
      );


    if (
      !Number.isFinite(
        amount
      ) ||
      amount <= 0
    ) {

      this.errorMessage =
        'Informe um valor de pagamento válido.';

      return;
    }


    if (
      amount >
      this.remainingAmount
    ) {

      this.errorMessage =
        'O pagamento não pode ser maior que o valor restante.';

      return;
    }


    const payload:
      RegisterPaymentPayload = {

      method:
        this.paymentForm
          .method,


      amount,


      notes:
        this.paymentForm
          .notes
          .trim() ||
        undefined,


      transactionCode:
        this.paymentForm
          .transactionCode
          .trim() ||
        undefined

    };


    this.saving = true;

    this.clearMessages();


    this.tableService
      .registerPayment(
        this.restaurantId,
        this.selectedSession.id,
        payload
      )
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe({

        next: () => {

          this.saving =
            false;


          this.showPaymentModal =
            false;


          this.successMessage =
            'Pagamento registrado com sucesso.';


          this.refreshSelectedSession();

          this.loadTables();

        },

        error: error => {

          this.saving =
            false;


          this.errorMessage =
            error?.error?.message ||
            'Não foi possível registrar o pagamento.';

        }

      });
  }


  /*
   * ==================================================
   * TRANSFERÊNCIA
   * ==================================================
   */

  get availableTransferTables():
    RestaurantTable[] {

    return this
      .tables
      .filter(
        table =>
          table.status ===
            'AVAILABLE' &&
          table.isActive !==
            false &&
          table.id !==
            this.selectedTable?.id
      );
  }


  openTransferModal(): void {

    if (
      !this.selectedTable ||
      !this.selectedSession
    ) {

      this.errorMessage =
        'Não foi possível identificar a comanda.';

      return;
    }


    if (
      [
        'CLOSED',
        'CANCELED'
      ].includes(
        this.selectedSession.status
      )
    ) {

      this.errorMessage =
        'Comandas encerradas não podem ser transferidas.';

      return;
    }


    this.transferTargetTableId =
      null;


    this.showTransferModal =
      true;
  }


  transferSession(): void {

    if (
      !this.restaurantId ||
      !this.selectedSession
    ) {
      return;
    }


    const targetTableId =
      Number(
        this.transferTargetTableId
      );


    if (
      !Number.isInteger(
        targetTableId
      ) ||
      targetTableId <= 0
    ) {

      this.errorMessage =
        'Selecione a mesa de destino.';

      return;
    }


    if (
      targetTableId ===
      this.selectedTable?.id
    ) {

      this.errorMessage =
        'Escolha uma mesa diferente da atual.';

      return;
    }


    this.saving = true;

    this.clearMessages();


    this.tableService
      .transferSession(
        this.restaurantId,
        this.selectedSession.id,
        targetTableId
      )
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe({

        next: () => {

          this.saving =
            false;


          this.showTransferModal =
            false;


          this.showSessionModal =
            false;


          this.transferTargetTableId =
            null;


          this.selectedTable =
            null;


          this.selectedSession =
            null;


          this.successMessage =
            'Comanda transferida com sucesso.';


          this.loadTables();

        },

        error: error => {

          this.saving =
            false;


          this.errorMessage =
            error?.error?.message ||
            'Não foi possível transferir a comanda.';

        }

      });
  }


  /*
   * ==================================================
   * CANCELAR PAGAMENTO
   * ==================================================
   */

  cancelPayment(
    paymentId: number
  ): void {

    if (
      !this.restaurantId ||
      !this.selectedSession
    ) {
      return;
    }


    const confirmed =
      window.confirm(
        'Deseja cancelar este pagamento?'
      );


    if (
      !confirmed
    ) {
      return;
    }


    this.saving = true;

    this.clearMessages();


    this.tableService
      .cancelPayment(
        this.restaurantId,
        this.selectedSession.id,
        paymentId
      )
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe({

        next: () => {

          this.saving =
            false;


          this.successMessage =
            'Pagamento cancelado.';


          this.refreshSelectedSession();

          this.loadTables();

        },

        error: error => {

          this.saving =
            false;


          this.errorMessage =
            error?.error?.message ||
            'Não foi possível cancelar o pagamento.';

        }

      });
  }


  /*
   * ==================================================
   * FECHAR COMANDA
   * ==================================================
   */

  closeSession(): void {

    if (
      !this.restaurantId ||
      !this.selectedSession
    ) {
      return;
    }


    if (
      this.remainingAmount >
      0.009
    ) {

      this.errorMessage =
        'A comanda precisa estar totalmente paga antes do fechamento.';

      return;
    }


    const confirmed =
      window.confirm(
        'Deseja fechar esta comanda e liberar a mesa?'
      );


    if (
      !confirmed
    ) {
      return;
    }


    this.saving = true;

    this.clearMessages();


    this.tableService
      .closeSession(
        this.restaurantId,
        this.selectedSession.id
      )
      .pipe(
        takeUntil(
          this.destroy$
        )
      )
      .subscribe({

        next: () => {

          this.saving =
            false;


          this.showSessionModal =
            false;


          this.selectedSession =
            null;


          this.selectedTable =
            null;


          this.successMessage =
            'Comanda fechada e mesa liberada.';


          this.loadTables();

        },

        error: error => {

          this.saving =
            false;


          this.errorMessage =
            error?.error?.message ||
            'Não foi possível fechar a comanda.';

        }

      });
  }


  /*
   * ==================================================
   * MODAIS
   * ==================================================
   */

  closeAllModals(): void {

    this.showOpenSessionModal =
      false;


    this.showSessionModal =
      false;


    this.showPaymentModal =
      false;


    this.showTransferModal =
      false;


    this.transferTargetTableId =
      null;


    this.selectedTable =
      null;


    this.selectedSession =
      null;
  }


  closeTransferModal(): void {

    this.showTransferModal =
      false;


    this.transferTargetTableId =
      null;
  }


  /*
   * ==================================================
   * FILTROS
   * ==================================================
   */

  get filteredTables():
    RestaurantTable[] {

    const search =
      this.normalizeText(
        this.searchText
      );


    return this
      .tables
      .filter(
        table => {

          const matchesStatus =
            !this.statusFilter ||
            table.status ===
              this.statusFilter;


          const searchableText =
            this.normalizeText(
              `${
                table.name
              } ${
                table.number || ''
              } ${
                table.area || ''
              }`
            );


          const matchesSearch =
            !search ||
            searchableText
              .includes(
                search
              );


          return (
            matchesStatus &&
            matchesSearch
          );

        }
      );
  }


  /*
   * ==================================================
   * CONTADORES
   * ==================================================
   */

  get availableCount():
    number {

    return this.countByStatus(
      'AVAILABLE'
    );
  }


  get occupiedCount():
    number {

    return this.countByStatus(
      'OCCUPIED'
    );
  }


  get billRequestedCount():
    number {

    return this.countByStatus(
      'BILL_REQUESTED'
    );
  }


  get awaitingPaymentCount():
    number {

    return this.countByStatus(
      'AWAITING_PAYMENT'
    );
  }


  /*
   * ==================================================
   * COMANDA
   * ==================================================
   */

  get orders():
    any[] {

    return (
      this.selectedSession
        ?.orders ||
      []
    );
  }


  get payments():
    any[] {

    return (
      this.selectedSession
        ?.payments ||
      []
    );
  }


  get totalAmount():
    number {

    return this.toNumber(
      this.selectedSession
        ?.summary
        ?.total
    );
  }


  get paidAmount():
    number {

    return this.toNumber(
      this.selectedSession
        ?.summary
        ?.paidAmount
    );
  }


  get remainingAmount():
    number {

    return this.toNumber(
      this.selectedSession
        ?.summary
        ?.remainingAmount
    );
  }


  get canRequestBill():
    boolean {

    return (
      this.selectedSession
        ?.status ===
      'OPEN'
    );
  }


  get canRegisterPayment():
    boolean {

    return Boolean(
      this.selectedSession &&
      [
        'BILL_REQUESTED',
        'AWAITING_PAYMENT'
      ].includes(
        this.selectedSession
          .status
      ) &&
      this.remainingAmount >
        0.009
    );
  }


  get canCloseSession():
    boolean {

    return Boolean(
      this.selectedSession &&
      this.remainingAmount <=
        0.009 &&
      this.selectedSession
        .status !==
        'CLOSED' &&
      this.selectedSession
        .status !==
        'CANCELED'
    );
  }


  /*
   * ==================================================
   * LABELS
   * ==================================================
   */

  getStatusLabel(
    status: string
  ): string {

    const labels:
      Record<
        string,
        string
      > = {

      AVAILABLE:
        'Disponível',

      OCCUPIED:
        'Ocupada',

      RESERVED:
        'Reservada',

      BILL_REQUESTED:
        'Conta solicitada',

      AWAITING_PAYMENT:
        'Aguardando pagamento',

      UNAVAILABLE:
        'Indisponível',

      OPEN:
        'Comanda aberta',

      CLOSED:
        'Fechada',

      CANCELED:
        'Cancelada'

    };


    return (
      labels[status] ||
      status
    );
  }


  getOrderStatusLabel(
    status: string
  ): string {

    const labels:
      Record<
        string,
        string
      > = {

      PENDING:
        'Recebido',

      ACCEPTED:
        'Aceito',

      PREPARING:
        'Preparando',

      READY:
        'Pronto',

      OUT_FOR_DELIVERY:
        'Saiu para entrega',

      DELIVERED:
        'Entregue',

      CANCELED:
        'Cancelado'

    };


    return (
      labels[status] ||
      status
    );
  }


  getPaymentMethodLabel(
    method: string
  ): string {

    const labels:
      Record<
        string,
        string
      > = {

      CASH:
        'Dinheiro',

      PIX:
        'Pix',

      DEBIT_CARD:
        'Cartão de débito',

      CREDIT_CARD:
        'Cartão de crédito',

      ONLINE:
        'Pagamento on-line',

      OTHER:
        'Outro'

    };


    return (
      labels[method] ||
      method
    );
  }


  /*
   * ==================================================
   * UTILITÁRIOS
   * ==================================================
   */

  toNumber(
    value:
      number |
      string |
      null |
      undefined
  ): number {

    const number =
      Number(
        value ||
        0
      );


    return Number
      .isFinite(
        number
      )
      ? number
      : 0;
  }


  trackTable(
    index: number,
    table:
      RestaurantTable
  ): number {

    return table.id;
  }


  private countByStatus(
    status: string
  ): number {

    return this
      .tables
      .filter(
        table =>
          table.status ===
          status
      )
      .length;
  }


  private normalizeText(
    value: string
  ): string {

    return String(
      value ||
      ''
    )
      .normalize(
        'NFD'
      )
      .replace(
        /[\u0300-\u036f]/g,
        ''
      )
      .toLowerCase()
      .trim();
  }


  private clearMessages():
    void {

    this.errorMessage = '';

    this.successMessage = '';
  }
}